/**
 * Conversation Hook
 * @implements FS-002.7 - AI PM Conversation
 * @implements FS-002.7.4 - Conversation History Persistence
 * @implements FS-002.7.8 - Conversation Real-Time Updates
 * @implements FS-002.7.9 - Conversation Input Behavior
 * @implements FS-002.7.10 - Conversation Message Pagination
 * @implements US-020 - AI PM Conversation UI
 *
 * Custom hook for managing AI PM conversation state, including:
 * - Fetching conversation history with pagination
 * - Sending messages
 * - Real-time message subscription
 * - Typing indicator state
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useSubscription, gql } from '@apollo/client';
import type { Message } from '@/components/conversation/MessageList';

/**
 * Initial number of messages to load
 * @implements FS-002.7.10 - Initial load: 50 messages
 */
const INITIAL_MESSAGE_LIMIT = 50;

/**
 * Number of messages to load on scroll
 * @implements FS-002.7.10 - Load more: 25 messages per scroll
 */
const LOAD_MORE_LIMIT = 25;

/**
 * GraphQL fragment for conversation message fields
 */
const CONVERSATION_MESSAGE_FIELDS = gql`
  fragment ConversationMessageFields on ConversationMessage {
    id
    conversationId
    senderType
    content
    createdAt
    referencedArtifacts {
      type
      id
    }
    agentInvocations {
      agentType
      launchedAt
      completedAt
      status
    }
  }
`;

/**
 * GraphQL query to get project conversations
 */
const GET_PROJECT_CONVERSATIONS = gql`
  query GetProjectConversations($projectId: ID!) {
    conversations(projectId: $projectId) {
      id
      projectId
      status
      createdAt
      lastActivityAt
    }
  }
`;

/**
 * GraphQL query to get conversation with messages
 */
const GET_CONVERSATION = gql`
  ${CONVERSATION_MESSAGE_FIELDS}
  query GetConversation($id: ID!, $limit: Int, $after: ID) {
    conversation(id: $id) {
      id
      projectId
      status
      createdAt
      lastActivityAt
      messages(limit: $limit, after: $after) {
        ...ConversationMessageFields
      }
    }
  }
`;

/**
 * GraphQL mutation to send a message
 */
const SEND_MESSAGE = gql`
  ${CONVERSATION_MESSAGE_FIELDS}
  mutation SendMessage($conversationId: ID!, $input: SendMessageInput!) {
    sendMessage(conversationId: $conversationId, input: $input) {
      ...ConversationMessageFields
    }
  }
`;

/**
 * GraphQL subscription for new messages
 */
const MESSAGE_RECEIVED = gql`
  ${CONVERSATION_MESSAGE_FIELDS}
  subscription MessageReceived($conversationId: ID!) {
    messageReceived(conversationId: $conversationId) {
      ...ConversationMessageFields
    }
  }
`;

/**
 * Conversation status enum
 */
export type ConversationStatus = 'ACTIVE' | 'ARCHIVED';

/**
 * Conversation summary type (from list query)
 */
export interface ConversationSummary {
  id: string;
  projectId: string;
  status: ConversationStatus;
  createdAt: string;
  lastActivityAt: string;
}

/**
 * Message type from GraphQL
 */
interface ConversationMessageData {
  id: string;
  conversationId: string;
  senderType: string;
  content: string;
  createdAt: string;
  referencedArtifacts?: Array<{
    type: string;
    id: string;
  }> | null;
  agentInvocations?: Array<{
    agentType: string;
    launchedAt: string;
    completedAt: string | null;
    status: string;
  }> | null;
}

/**
 * Conversation data from GraphQL query
 */
interface ConversationData {
  conversation: {
    id: string;
    projectId: string;
    status: ConversationStatus;
    createdAt: string;
    lastActivityAt: string;
    messages: ConversationMessageData[];
  } | null;
}

/**
 * Input parameters for useConversation hook
 */
export interface UseConversationInput {
  /** Project ID to get conversations for */
  projectId: string;
  /** Whether the project is archived (affects input state) */
  isProjectArchived?: boolean;
  /** Callback when user sends a message (for onboarding tracking) */
  onMessageSent?: () => void;
  /** Callback when AI PM responds (for onboarding tracking) */
  onAiPmResponse?: () => void;
}

/**
 * Result type for useConversation hook
 */
export interface UseConversationResult {
  /** Active conversation ID (if any) */
  conversationId: string | null;
  /** Array of messages in the conversation */
  messages: Message[];
  /** Whether the initial data is loading */
  isLoading: boolean;
  /** Whether older messages are being loaded */
  isLoadingMore: boolean;
  /** Whether a message is being sent */
  isSending: boolean;
  /** Whether the AI PM is generating a response */
  isTyping: boolean;
  /** Whether there are more messages to load */
  hasMore: boolean;
  /** Whether the conversation is read-only (archived) */
  isReadOnly: boolean;
  /** Error message if any */
  error: string | null;
  /** Send a message to the conversation */
  sendMessage: (content: string) => Promise<void>;
  /** Load more (older) messages */
  loadMoreMessages: () => Promise<void>;
}

/**
 * useConversation Hook
 * @implements FS-002.7 - AI PM Conversation
 *
 * Manages conversation state including:
 * - Fetching conversation and messages
 * - Sending messages
 * - Real-time subscription for new messages
 * - Typing indicator while waiting for AI PM response
 *
 * @example
 * ```tsx
 * const {
 *   messages,
 *   isLoading,
 *   isTyping,
 *   sendMessage,
 *   loadMoreMessages,
 * } = useConversation({ projectId: 'proj-123' });
 * ```
 */
export const useConversation = ({
  projectId,
  isProjectArchived = false,
  onMessageSent,
  onAiPmResponse,
}: UseConversationInput): UseConversationResult => {
  // Local state
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [oldestMessageId, setOldestMessageId] = useState<string | null>(null);

  /**
   * Query for project conversations
   * @implements FS-002.7.4 - Conversation History Persistence
   */
  const {
    data: conversationsData,
    loading: conversationsLoading,
    error: conversationsError,
  } = useQuery(GET_PROJECT_CONVERSATIONS, {
    variables: { projectId },
    skip: !projectId,
    fetchPolicy: 'cache-and-network',
  });

  // Set the active conversation ID when conversations are loaded
  useEffect(() => {
    if (conversationsData?.conversations?.length > 0) {
      // Use the most recent active conversation
      const activeConversation = conversationsData.conversations.find(
        (c: ConversationSummary) => c.status === 'ACTIVE',
      );
      if (activeConversation) {
        setConversationId(activeConversation.id);
      } else {
        // Fall back to first conversation if none are active
        setConversationId(conversationsData.conversations[0].id);
      }
    }
  }, [conversationsData]);

  /**
   * Query for conversation with messages
   * @implements FS-002.7.10 - Initial load: 50 most recent messages
   */
  const {
    data: conversationData,
    loading: conversationLoading,
    error: conversationError,
    fetchMore,
  } = useQuery(GET_CONVERSATION, {
    variables: {
      id: conversationId,
      limit: INITIAL_MESSAGE_LIMIT,
    },
    skip: !conversationId,
    fetchPolicy: 'cache-and-network',
  });

  // Track oldest message ID for pagination cursor
  useEffect(() => {
    const messages = conversationData?.conversation?.messages || [];
    if (messages.length > 0) {
      setOldestMessageId(messages[0].id);
      // If we received fewer messages than requested, we've loaded all
      if (messages.length < INITIAL_MESSAGE_LIMIT) {
        setHasMore(false);
      }
    }
  }, [conversationData?.conversation?.messages]);

  /**
   * Send message mutation
   * @implements FS-002.7.9 - Conversation Input Behavior
   * @implements TS-088 - Onboarding flow restrictions (calls onMessageSent)
   */
  const [sendMessageMutation, { loading: sendingMessage }] = useMutation(
    SEND_MESSAGE,
    {
      onCompleted: (data) => {
        // After sending, show typing indicator for AI response
        if (data?.sendMessage) {
          setIsTyping(true);
          // Notify that a message was sent (for onboarding tracking)
          onMessageSent?.();
        }
      },
      update: (cache, { data }) => {
        // Optimistic update: add the new message to the cache
        if (data?.sendMessage && conversationId) {
          const existingData = cache.readQuery<ConversationData>({
            query: GET_CONVERSATION,
            variables: { id: conversationId, limit: INITIAL_MESSAGE_LIMIT },
          });

          if (existingData?.conversation) {
            cache.writeQuery({
              query: GET_CONVERSATION,
              variables: { id: conversationId, limit: INITIAL_MESSAGE_LIMIT },
              data: {
                conversation: {
                  ...existingData.conversation,
                  messages: [
                    ...existingData.conversation.messages,
                    data.sendMessage,
                  ],
                },
              },
            });
          }
        }
      },
    },
  );

  /**
   * Subscribe to new messages
   * @implements FS-002.7.8 - Conversation Real-Time Updates
   * @implements TS-088 - Onboarding flow restrictions (calls onAiPmResponse)
   */
  const { error: subscriptionError } = useSubscription(MESSAGE_RECEIVED, {
    variables: { conversationId },
    skip: !conversationId,
    onData: ({ data }) => {
      const newMessage = data?.data?.messageReceived;
      if (newMessage) {
        // Stop typing indicator when AI PM responds
        if (newMessage.senderType === 'AI_PM') {
          setIsTyping(false);
          // Notify that AI PM responded (for onboarding tracking)
          onAiPmResponse?.();
        }
      }
    },
  });

  /**
   * Load more (older) messages
   * @implements FS-002.7.10 - Load more: 25 messages per scroll
   */
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const loadMoreMessages = useCallback(async (): Promise<void> => {
    if (!conversationId || !oldestMessageId || isLoadingMore || !hasMore) {
      return;
    }

    setIsLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          id: conversationId,
          limit: LOAD_MORE_LIMIT,
          after: oldestMessageId,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.conversation?.messages) {
            setHasMore(false);
            return prev;
          }

          const newMessages = fetchMoreResult.conversation.messages;
          if (newMessages.length === 0) {
            setHasMore(false);
            return prev;
          }

          if (newMessages.length < LOAD_MORE_LIMIT) {
            setHasMore(false);
          }

          // Update oldest message ID for next pagination
          setOldestMessageId(newMessages[0].id);

          return {
            conversation: {
              ...prev.conversation,
              messages: [...newMessages, ...prev.conversation.messages],
            },
          };
        },
      });
    } finally {
      setIsLoadingMore(false);
    }
  }, [conversationId, oldestMessageId, isLoadingMore, hasMore, fetchMore]);

  /**
   * Send a message
   * @implements FS-002.7.9 - Conversation Input Behavior
   */
  const sendMessage = useCallback(
    async (content: string) => {
      if (!conversationId || isProjectArchived) {
        throw new Error(
          isProjectArchived
            ? 'Cannot send messages in archived projects'
            : 'No active conversation',
        );
      }

      await sendMessageMutation({
        variables: {
          conversationId,
          input: { content },
        },
      });
    },
    [conversationId, isProjectArchived, sendMessageMutation],
  );

  /**
   * Transform messages for the MessageList component
   */
  const messages: Message[] = useMemo(() => {
    const rawMessages = conversationData?.conversation?.messages || [];
    return rawMessages.map(
      (msg: {
        id: string;
        senderType: string;
        content: string;
        createdAt: string;
        referencedArtifacts?: unknown;
        agentInvocations?: unknown;
      }) => ({
        id: msg.id,
        senderType: msg.senderType as Message['senderType'],
        content: msg.content,
        createdAt: msg.createdAt,
        referencedArtifacts:
          msg.referencedArtifacts as Message['referencedArtifacts'],
        agentInvocations: msg.agentInvocations as Message['agentInvocations'],
      }),
    );
  }, [conversationData?.conversation?.messages]);

  /**
   * Determine error state
   */
  const error = useMemo(() => {
    if (conversationsError) {
      return `Failed to load conversations: ${conversationsError.message}`;
    }
    if (conversationError) {
      return `Failed to load messages: ${conversationError.message}`;
    }
    if (subscriptionError) {
      // Log subscription error but don't show to user (graceful degradation)
      console.warn('Subscription error:', subscriptionError);
      return null;
    }
    return null;
  }, [conversationsError, conversationError, subscriptionError]);

  /**
   * Determine if conversation is read-only
   * @implements FS-002.7.6 - AI PM Conversation in Archived Projects
   */
  const isReadOnly = useMemo(() => {
    if (isProjectArchived) {
      return true;
    }
    const conversationStatus = conversationData?.conversation?.status;
    return conversationStatus === 'ARCHIVED';
  }, [isProjectArchived, conversationData?.conversation?.status]);

  return {
    conversationId,
    messages,
    isLoading: conversationsLoading || conversationLoading,
    isLoadingMore,
    isSending: sendingMessage,
    isTyping,
    hasMore,
    isReadOnly,
    error,
    sendMessage,
    loadMoreMessages,
  };
};
